// src/pages/clientes/ClientesPage.tsx
import { useState, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button, Avatar } from '@/components/ui';
import { QDataTable } from '@/components/tables';
import { Plus } from 'lucide-react';
import { useClientes } from '@/hooks/queries/useClientes';
import { useScreenState } from '@/hooks/useScreenState';
import { buildTableProps } from '@/lib/screen-utils';
import { clienteScreenDef } from '@/screen-definitions';
import { ClienteFormModal } from './components/ClienteFormModal';
import { DeleteClienteModal } from './components/DeleteClienteModal';
import type { Cliente } from '@/types';

export function ClientesPage() {
  const navigate = useNavigate();
  const screenState = useScreenState(clienteScreenDef);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);

  // Server-side query: merge screen params + entity-specific filter transforms
  const apiParams = useMemo(() => ({
    ...screenState.queryParams,
    activo: screenState.filterValues.activo === 'true'
      ? true
      : screenState.filterValues.activo === 'false'
        ? false
        : undefined,
  }), [screenState.queryParams, screenState.filterValues]);

  const query = useClientes(apiParams);

  // Action handlers mapped by key (from ScreenRowAction.action)
  const handleNavigate = useCallback((c: Cliente) => navigate(`/clientes/${c.id}`), [navigate]);
  const handleEdit = useCallback((c: Cliente) => { setSelectedCliente(c); setIsFormOpen(true); }, []);
  const handleDelete = useCallback((c: Cliente) => { setSelectedCliente(c); setIsDeleteOpen(true); }, []);

  const tableProps = buildTableProps(clienteScreenDef, screenState, query, {
    getId: (c: Cliente) => c.id,
    actionHandlers: {
      navigate: handleNavigate,
      edit: handleEdit,
      delete: handleDelete,
    },
    customRenderers: {
      nombre: (row: Cliente) => (
        <div className="flex items-center gap-3">
          <Avatar fallback={row.nombre} size="sm" />
          <Link to={`/clientes/${row.id}`} className="font-medium hover:text-primary">
            {row.nombre}
          </Link>
        </div>
      ),
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Clientes</h1>
          <p className="text-muted-foreground">Gestiona los clientes del sistema</p>
        </div>
      </div>

      <QDataTable
        {...tableProps}
        headerActions={
          <Button onClick={() => setIsFormOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Cliente
          </Button>
        }
      />

      <ClienteFormModal
        open={isFormOpen}
        onClose={() => { setSelectedCliente(null); setIsFormOpen(false); }}
        cliente={selectedCliente}
      />

      <DeleteClienteModal
        open={isDeleteOpen}
        onClose={() => { setSelectedCliente(null); setIsDeleteOpen(false); }}
        cliente={selectedCliente}
      />
    </div>
  );
}

export default ClientesPage;
